import { useState } from "react";
import type { Lender, Outcome, OutcomeStatus } from "../types";
import { Icon } from "./Icon";

interface Props {
  lender: Lender;
  outcomes: Outcome[];
  onLog: (o: Omit<Outcome, "at">) => Promise<unknown> | void;
}

const DECLINE_REASONS = [
  "Insufficient income",
  "Credit history",
  "Existing debt obligations",
  "Address mismatch",
  "Identity verification failed",
  "Other",
];

interface Draft {
  status: OutcomeStatus | null;
  amount: string;
  reason: string;
  note: string;
}

const EMPTY: Draft = { status: null, amount: "", reason: "", note: "" };

export function OutcomeLogger({ lender, outcomes, onLog }: Props) {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const lenderOutcomes = outcomes.filter((o) => o.lender === lender.id);

  const submit = async () => {
    if (!draft.status || submitting) return;
    setSubmitting(true);
    setErr(null);
    try {
      await onLog({
        lender: lender.id,
        status: draft.status,
        amount: draft.amount ? Number(draft.amount.replace(/[^0-9]/g, "")) : null,
        reason: draft.reason || undefined,
        note: draft.note || undefined,
      });
      setDraft(EMPTY);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to log outcome");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lender__section">
      <div className="lender__section-head">
        <h3>Log outcome</h3>
        <span className="lender__count">
          {lenderOutcomes.length} prior {lenderOutcomes.length === 1 ? "attempt" : "attempts"}
        </span>
      </div>

      <div className="outcome">
        <div className="outcome__row">
          <button
            className={`outcome__btn outcome__btn--approve ${draft.status === "approved" ? "is-active" : ""}`}
            onClick={() => setDraft((d) => ({ ...d, status: "approved" }))}
          >
            <Icon.Check width={14} height={14} /> Approved
          </button>
          <button
            className={`outcome__btn outcome__btn--decline ${draft.status === "declined" ? "is-active" : ""}`}
            onClick={() => setDraft((d) => ({ ...d, status: "declined" }))}
          >
            Declined
          </button>
          <button
            className={`outcome__btn outcome__btn--pending ${draft.status === "pending" ? "is-active" : ""}`}
            onClick={() => setDraft((d) => ({ ...d, status: "pending" }))}
          >
            Pending
          </button>
        </div>

        {draft.status === "approved" && (
          <label className="outcome__field">
            <span>Approved amount</span>
            <div className="outcome__money">
              <span>$</span>
              <input
                inputMode="numeric"
                value={draft.amount}
                onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value.replace(/[^0-9]/g, "") }))}
                placeholder="38500"
              />
            </div>
          </label>
        )}

        {draft.status === "declined" && (
          <label className="outcome__field">
            <span>Decline reason (optional)</span>
            <select
              value={draft.reason}
              onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
            >
              <option value="">Select…</option>
              {DECLINE_REASONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
        )}

        {draft.status && (
          <label className="outcome__field">
            <span>Note (optional)</span>
            <input
              value={draft.note}
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
              placeholder={
                draft.status === "approved"
                  ? "e.g. 60-month term at 9.99%"
                  : "e.g. patient declined to retry"
              }
            />
          </label>
        )}

        {err && <div className="form__error">{err}</div>}

        {draft.status && (
          <div className="outcome__submit">
            <button className="btn btn--ghost" onClick={() => setDraft(EMPTY)} disabled={submitting}>
              Cancel
            </button>
            <button className="btn btn--solid" onClick={submit} disabled={submitting}>
              {submitting ? "Logging…" : "Log outcome"}
            </button>
          </div>
        )}
      </div>

      {lenderOutcomes.length > 0 && (
        <ul className="outcome__history">
          {lenderOutcomes
            .slice()
            .reverse()
            .map((o, i) => (
              <li key={o.id ?? i} className={`outcome__hist outcome__hist--${o.status}`}>
                <span className="outcome__hist-status">
                  {o.status === "approved" && (
                    <>
                      <Icon.Check width={11} height={11} /> Approved
                    </>
                  )}
                  {o.status === "declined" && <>Declined</>}
                  {o.status === "pending" && <>Pending</>}
                </span>
                {o.amount != null && (
                  <span className="outcome__hist-amt">${o.amount.toLocaleString()}</span>
                )}
                {o.reason && <span className="outcome__hist-meta">· {o.reason}</span>}
                {o.note && <span className="outcome__hist-note">{o.note}</span>}
                <span className="outcome__hist-time">
                  {new Date(o.at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                </span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
